import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AgricolaService } from '../../../../services/agricola.service';
import { MeService } from '../../../../services/me.service';

export interface UpdateClientDialogData {
  address: string;
  phone: string;
  mobile: string;
  email: string;
  dui: string;
  memberId: number;
  firstName: string;
  middleName: string | null;
  lastName: string;
  lastName2: string | null;
  marriedName: string | null;
  birthday: string | null;
  gender: number | null;
  relationshipStatus: number | null;
  memberClassification: number | null;
  phone2: string;
  taxNumber: string;
  frontingId: number;
  planName: string;
  assistanceCode: number;
  assistanceSaleCode: string;
  stateId: number | null;
  stateDistrictId: number | null;
  clientStatus: number | null;
}

@Component({
  selector: 'app-agricola-update-client-dialog',
  templateUrl: './agricola-update-client-dialog.component.html',
  styleUrls: ['./agricola-update-client-dialog.component.css'],
  standalone: false
})
export class AgricolaUpdateClientDialogComponent implements OnInit {

  form!: FormGroup;
  isSubmitting = false;

  typologyOptions:    { value: string; label: string }[] = [];
  subtypologyOptions: { value: string; label: string }[] = [];
  stateOptions:       { value: string; label: string }[] = [];
  districtOptions:    { value: string; label: string }[] = [];
  agencyOptions:      { value: string; label: string }[] = [];

  genderOptions = [
    { value: '0', label: 'Femenino' },
    { value: '1', label: 'Masculino' }
  ];

  relationshipStatusOptions = [
    { value: '1', label: 'Soltero' },
    { value: '2', label: 'Casado' },
    { value: '3', label: 'Divorciado' },
    { value: '4', label: 'Viudo' }
  ];

  memberClassificationOptions = [
    { value: '',    label: 'Normal' },
    { value: '30',  label: 'VIP' },
    { value: '20',  label: 'Top Offender' },
    { value: '10',  label: 'Black List' },
    { value: '50',  label: 'Discapacitado' },
    { value: '60',  label: 'Top Offender Gasolina' },
    { value: '250', label: 'Colaborador-Sponsor' }
  ];

  private subtypologiesMap = new Map<number, { value: string; label: string }[]>();

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AgricolaUpdateClientDialogComponent>,
    private agricolaService: AgricolaService,
    private meService: MeService,
    @Inject(MAT_DIALOG_DATA) public data: UpdateClientDialogData
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      typologyId:           [null, Validators.required],
      subtypologyId:        [null, Validators.required],
      firstName:            [this.data.firstName,           Validators.required],
      middleName:           [this.data.middleName   ?? ''],
      lastName:             [this.data.lastName,            Validators.required],
      lastName2:            [this.data.lastName2    ?? ''],
      marriedName:          [this.data.marriedName  ?? ''],
      birthday:             [this.data.birthday,            Validators.required],
      gender:               [this.data.gender,              Validators.required],
      relationshipStatus:   [this.data.relationshipStatus,  Validators.required],
      nationalId:           [this.data.dui,                 Validators.required],
      mobile:               [this.data.mobile,              Validators.required],
      phone1:               [this.data.phone,               Validators.required],
      phone2:               [this.data.phone2       ?? ''],
      existEmail:           [this.data.email !== 'notiene@cuentame.net' && !!this.data.email],
      email:                [this.data.email, [Validators.required, Validators.email]],
      memberClassification: [{ value: this.data.memberClassification, disabled: true }],
      address:              [this.data.address      ?? ''],
      stateId:              [this.data.stateId      ?? null],
      stateDistrictId:      [this.data.stateDistrictId ?? null],
      taxNumber:            [this.data.taxNumber    ?? ''],
      notes:                ['Se ACTUALIZA información a solicitud del afiliado', Validators.required],
      uidViciDial:          [{ value: '', disabled: true }],
      agencyCode:           [null]
    });

    this.loadCatalogEquivalences();
    this.loadStates();
    this.loadAgencies();
    this.subscribeToFormChanges();
    this.applyEmailInitialState();
  }

  private loadAgencies(): void {
    this.agricolaService.getAgencies().subscribe({
      next: (res: any) => {
        const raw = res?.data?.data ?? res?.data ?? res;
        const rows: any[] = Array.isArray(raw) ? raw : [];
        this.agencyOptions = rows
          .filter(r => r?.code)
          .map(r => ({
            value: String(r.code),
            label: String(r.description_es || r.description_en || r.code)
          }));
      }
    });
  }

  private applyEmailInitialState(): void {
    const emailCtrl = this.form.get('email')!;
    if (!this.form.get('existEmail')!.value) {
      emailCtrl.disable();
      emailCtrl.setValue('notiene@cuentame.net');
    }
  }

  private loadCatalogEquivalences(): void {
    this.agricolaService.getCatalogEquivalences().subscribe({
      next: (res: any) => {
        const raw = res?.data?.data ?? res?.data ?? res;
        const items: any[] = Array.isArray(raw) ? raw : [];
        const apoloEntry = items.find(
          (e: any) => e.value_ === 'crm_typology_apolo' && e.to_ === '10'
        );
        if (!apoloEntry?.typologies) return;
        this.typologyOptions = apoloEntry.typologies.map((t: any) => ({
          value: String(t.id),
          label: t.description
        }));
        apoloEntry.typologies.forEach((t: any) => {
          this.subtypologiesMap.set(
            t.id,
            (t.subtypologies ?? []).map((s: any) => ({ value: String(s.id), label: s.description }))
          );
        });

        const modEntry = apoloEntry.typologies.find(
          (t: any) => t.description === 'Modificación de Datos'
        );
        if (modEntry) {
          this.form.get('typologyId')!.setValue(String(modEntry.id));
          this.form.get('typologyId')!.disable();
        }
      }
    });
  }

  private loadStates(): void {
    this.agricolaService.getStates(4).subscribe({
      next: states => {
        this.stateOptions = states.map(s => ({ value: s.code, label: s.description }));
        const stateId = this.form.get('stateId')!.value;
        if (stateId) {
          // Force the select to re-evaluate now that options are available
          this.form.get('stateId')!.setValue(stateId, { emitEvent: false });
          const districtId = this.form.get('stateDistrictId')!.value;
          this.loadDistricts(stateId, districtId);
        }
      }
    });
  }

  private loadDistricts(stateId: string | number, preselectDistrictId?: string | number | null): void {
    this.agricolaService.getSubstates(4, stateId).subscribe({
      next: districts => {
        this.districtOptions = districts.map(d => ({ value: d.code, label: d.description }));
        if (preselectDistrictId) {
          // Force the select to re-evaluate now that options are available
          this.form.get('stateDistrictId')!.setValue(preselectDistrictId, { emitEvent: false });
        }
      }
    });
  }

  private subscribeToFormChanges(): void {
    this.form.get('typologyId')!.valueChanges.subscribe(id => {
      this.subtypologyOptions = this.subtypologiesMap.get(Number(id)) ?? [];
      this.form.get('subtypologyId')!.setValue(null, { emitEvent: false });
    });

    this.form.get('stateId')!.valueChanges.subscribe(stateId => {
      this.districtOptions = [];
      this.form.get('stateDistrictId')!.setValue(null, { emitEvent: false });
      if (stateId) this.loadDistricts(stateId, null);
    });

    this.form.get('existEmail')!.valueChanges.subscribe(hasEmail => {
      const emailCtrl = this.form.get('email')!;
      if (!hasEmail) {
        emailCtrl.disable();
        emailCtrl.setValue('notiene@cuentame.net');
      } else {
        emailCtrl.enable();
      }
    });
  }

  onSubmit(): void {
    if (this.form.invalid) return;
    const f = this.form.getRawValue();
    const today = new Date().toISOString().split('T')[0];

    const payload = {
      country_id:            4,
      sponsor_id:            'ESR98',
      fronting_id:           this.data.frontingId,
      username:              this.meService.currentUser?.email ?? '',
      userId:                this.meService.currentUser?.id   ?? '',
      guid:                  '',
      ucontact_did:          '',
      account_id:            this.data.memberId,
      personal_id:           f.nationalId,
      first_name:            f.firstName,
      middle_name:           f.middleName  || null,
      last_name:             f.lastName,
      last_name_2:           f.lastName2   || null,
      married_name:          f.marriedName || null,
      formal_name:           [f.firstName, f.middleName, f.lastName, f.lastName2].filter(Boolean).join(' '),
      transaction:           'update_assistance_customer_data',
      transaction_type:      'CIC',
      transaction_date:      today,
      typology_id:           f.typologyId   ? Number(f.typologyId)   : null,
      subtypology_id:        f.subtypologyId ? Number(f.subtypologyId) : null,
      assistance_code:       this.data.assistanceCode,
      birthday:              this.toIsoDate(f.birthday),
      gender:                f.gender !== '' && f.gender != null ? Number(f.gender) : null,
      relationship_status:   f.relationshipStatus ? Number(f.relationshipStatus) : null,
      national_id:           f.nationalId,
      mobile:                f.mobile,
      phone1:                f.phone1,
      phone2:                f.phone2 || '',
      exist_email:           f.existEmail,
      email:                 f.email,
      member_classification: f.memberClassification !== '' ? Number(f.memberClassification) : null,
      country:               4,
      address:               f.address,
      state_id:              f.stateId         || '',
      state_district_id:     f.stateDistrictId || '',
      tax_number:            f.taxNumber       || '',
      client_status:         this.data.clientStatus,
      notes:                 f.notes           || '',
      UidViciDial:           f.uidViciDial     || '',
      agency_id:             f.agencyCode      || '',
      agency_name:           this.agencyOptions.find(a => a.value === f.agencyCode)?.label || '',
      agency_user:           this.meService.currentUser?.name || this.meService.currentUser?.email || '',
      agency_user_id:        null,
      assistance_sale_code:  this.data.assistanceSaleCode,
      elapsedTime:           0,
      plan:                  this.data.planName
    };

    this.isSubmitting = true;
    this.agricolaService.chubbTransaction(payload).subscribe({
      next: () => { this.isSubmitting = false; this.dialogRef.close({ success: true }); },
      error: () => { this.isSubmitting = false; this.dialogRef.close({ success: false }); }
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  private toIsoDate(value: string | null | undefined): string | null {
    if (!value) return null;
    const slash = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slash) {
      return `${slash[3]}-${slash[2].padStart(2, '0')}-${slash[1].padStart(2, '0')}`;
    }
    return value; // already YYYY-MM-DD or unknown — pass through
  }
}
